'use client'

import { FormError } from '@/components/forms/FormError'
import { FormItem } from '@/components/forms/FormItem'
import { Message } from '@/components/Message'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/providers/Auth'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import React, { useCallback, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslations } from 'next-intl'

type FormData = {
  email: string
  password: string
}

export const LoginForm: React.FC = () => {
  const searchParams = useSearchParams()
  const allParams = searchParams.toString() ? `?${searchParams.toString()}` : ''
  const redirect = useRef(searchParams.get('redirect'))
  const { login } = useAuth()
  const router = useRouter()
  const [error, setError] = React.useState<null | string>(null)
  const t = useTranslations('auth')

  const {
    formState: { errors, isLoading },
    handleSubmit,
    register,
  } = useForm<FormData>()

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        await login(data)

        // Handle redirect
        if (searchParams.get('redirect')) {
          const redirectPath = searchParams.get('redirect')
          // If path starts with / and not /en/ or /de/ (ignoring edge cases for now as v2 did)
          if (
            redirectPath &&
            redirectPath.startsWith('/') &&
            !redirectPath.startsWith('/en/') &&
            !redirectPath.startsWith('/de/')
          ) {
            // Get current locale from window or just defaulting to en/de check based on current path?
            // Better: utilize the router but we need to know the current locale.
            // Since this is client side, we can guess or just let the middleware handle it?
            // v2 logic:
            const pathSegments = window.location.pathname.split('/')
            const currentLocale = ['en', 'de'].includes(pathSegments[1]) ? pathSegments[1] : 'en'
            router.refresh()
            router.push(`/${currentLocale}${redirectPath}`)
          } else {
            router.refresh()
            router.push(redirectPath as string)
          }
        } else {
          // Default redirect
          const pathSegments = window.location.pathname.split('/')
          const currentLocale = ['en', 'de'].includes(pathSegments[1]) ? pathSegments[1] : 'en'
          router.refresh()
          router.push(`/${currentLocale}/account`)
        }
      } catch (_) {
        setError(t('credentialsError'))
      }
    },
    [login, router, searchParams, t],
  )

  return (
    <form className="" onSubmit={handleSubmit(onSubmit)}>
      <Message className="classes.message" error={error} />
      <div className="flex flex-col gap-8">
        <FormItem>
          <Label htmlFor="email">{t('email')}</Label>
          <Input id="email" type="email" {...register('email', { required: t('emailRequired') })} />
          {errors.email && <FormError message={errors.email.message} />}
        </FormItem>

        <FormItem>
          <Label htmlFor="password">{t('password')}</Label>
          <Input
            id="password"
            type="password"
            {...register('password', { required: t('passwordRequired') })}
          />
          {errors.password && <FormError message={errors.password.message} />}
        </FormItem>

        <div className="text-primary/70 mb-6 prose prose-a:hover:text-primary dark:prose-invert">
          <p>
            {t('forgotPassword')}{' '}
            <Link href={`/forgot-password${allParams}`}>{t('clickHereToReset')}</Link>
          </p>
        </div>
      </div>

      <div className="flex gap-4 justify-between">
        <Button asChild variant="outline" size="lg">
          <Link
            href={`/create-account${allParams}`}
            className="grow max-w-[50%] text-black dark:text-white"
          >
            {t('createAnAccount')}
          </Link>
        </Button>
        <Button className="grow" disabled={isLoading} size="lg" type="submit" variant="default">
          {isLoading ? t('loggingIn') : t('login')}
        </Button>
      </div>
    </form>
  )
}
